import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./VenueListDetails.scss";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import areanaImg from "../../../../assets/areanaDefault.png";
import UserContext from "../../../../context/UserContext";
import PaymentModalStep from "../../../PaymentModal/PaymentModalStep";
import Loader from "../../../../components/Loader/Loader";
import FriendForm from "./FriendForm";
import DropDownSport from "./DropDownSport";
import TimeDropdown from "./TimeDropdown";
import BillDetailsModal from "../../BillingComp/BillDetailsModal";
import BillingDetailsGuest from "./BillingDetailsGuest";
import MemberShipPlan from "../../NewHomePage/MemberShipPlan";

function VenueListDetails() {
  const { facilityId } = useParams();
  const { userDetail } = useContext(UserContext);
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isBillModalOpen, setIsBillModalOpen] = useState(false);
  const [initialStep, setInitialStep] = useState(5);
  const [friendsList, setFriendsList] = useState([]);
  const [bringingFriend, setBringingFriend] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const datePickerRef = useRef(null);

  // Form state
  const [selectedSport, setSelectedSport] = useState("");
  const [courtType, setCourtType] = useState("");
  const [date, setDate] = useState(new Date());
  const [startTime, setStartTime] = useState("");
  const [duration, setDuration] = useState(1);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [playersReq, setPlayersReq] = useState(0);
  const [courtsReq, setCourtsReq] = useState(0);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => setIsModalOpen(true);
  const closeModalNew = () => setIsModalOpen(false);

  // Helper function to check if trial is active
  const isTrialActive = () => {
    if (!userDetail?.trial_end_date) return false;
    const today = new Date();
    const [day, month, year] = userDetail.trial_end_date.split("-");
    const trialEndDate = new Date(`${year}-${month}-${day}`);
    if (isNaN(trialEndDate.getTime())) {
      console.error("Invalid Trial End Date:", userDetail.trial_end_date);
      return false;
    }
    return trialEndDate >= today;
  };

  const handleNoofPlayersChange = (change) => {
    if (!facility || !selectedSport) return;

    const sportDetails = facility.sport_info[selectedSport];
    if (!sportDetails) return;

    const courtTypeDetails = isFootballOrCricket
      ? sportDetails[courtType]
      : sportDetails;

    if (!courtTypeDetails) return;

    const { min_players, max_players } = courtTypeDetails;
    const scaledMinPlayers = min_players * courtsReq;
    const scaledMaxPlayers = max_players * courtsReq;

    setPlayersReq((prev) => {
      const newPlayersReq = prev + change;
      if (newPlayersReq < scaledMinPlayers) return scaledMinPlayers;
      if (newPlayersReq > scaledMaxPlayers) {
        toast.warn(`Maximum players allowed: ${scaledMaxPlayers}`);
        return scaledMaxPlayers;
      }
      return newPlayersReq;
    });
  };

  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 7);

  const fetchFacilityDetails = async () => {
    setLoading(true);
    setError(null);
    const url = `https://ballersapi.ballerslife.co/api/v1/memberships/sport_facility/get_facility_details?facility_id=${facilityId}`;
    const accessToken = window.localStorage.getItem("access_token");
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (response.status === 200) {
        setFacility(data);
        if (data.available_sports && data.available_sports.length > 0) {
          setSelectedSport(data.available_sports[0]);
          if (["football", "cricket"].includes(data.available_sports[0])) {
            const sportInfo = data.sport_info[data.available_sports[0]];
            const firstCourtType = Object.keys(sportInfo)[0];
            setCourtType(firstCourtType);
          } else {
            setCourtType("");
          }
        }
      } else {
        setError("Failed to fetch facility details. Please try again.");
      }
    } catch (err) {
      setError("An error occurred while fetching facility details.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async (customDuration = duration) => {
    if (
      !facility ||
      !selectedSport ||
      !date ||
      (["football", "cricket"].includes(selectedSport) && !courtType)
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }

    const url = `https://ballersapi.ballerslife.co/api/v1/memberships/bookings/slots?facility_id=${facilityId}&date=${formatDate(
      date
    )}&sport=${selectedSport}&court_type=${courtType}&duration=${customDuration}`;
    const accessToken = window.localStorage.getItem("access_token");

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();
      if (response.status === 200) {
        const rawSlots = Array.isArray(data.available_slots)
          ? data.available_slots
          : [];
        let filteredSlots = [...rawSlots];

        const today = new Date();
        if (
          date.toDateString() === today.toDateString() &&
          rawSlots.length > 0
        ) {
          const currentTime = today.toTimeString().slice(0, 5);
          filteredSlots = rawSlots.filter((slot) => {
            const slotTime = slot.start_time ? slot.start_time.slice(0, 5) : "";
            return slotTime >= currentTime;
          });
        }

        setAvailableSlots(filteredSlots);

        if (filteredSlots.length > 0) {
          const slotTimes = filteredSlots.map((slot) => slot.start_time);
          if (startTime && slotTimes.includes(startTime)) {
            const selectedSlot = filteredSlots.find(
              (slot) => slot.start_time === startTime
            );
            setCourtsReq(1);
          } else {
            const firstSlot = filteredSlots[0];
            setStartTime(firstSlot.start_time || "");
            setCourtsReq(1);
          }
        } else {
          setStartTime("");
          setCourtsReq(0);
        }
      } else {
        setAvailableSlots([]);
        setStartTime("");
        setCourtsReq(0);
        toast.error(data.detail || "Failed to fetch available slots.");
      }
    } catch (err) {
      setAvailableSlots([]);
      setStartTime("");
      setCourtsReq(0);
      toast.error("An error occurred while fetching available slots.");
    }
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatTime = (time) => {
    const [hour, minute] = time.split(":");
    const hourNum = parseInt(hour, 10);
    const period = hourNum >= 12 ? "PM" : "AM";
    const formattedHour = hourNum % 12 || 12;
    return `${formattedHour} ${period}`;
  };

  const handleSportChange = (e) => {
    const sport = e.target.value;
    setSelectedSport(sport);
    setAvailableSlots([]);
    setStartTime("");
    setCourtsReq(0);
    setPlayersReq(0);
    if (["football", "cricket"].includes(sport) && facility) {
      const sportInfo = facility.sport_info[sport];
      const firstCourtType = Object.keys(sportInfo)[0];
      setCourtType(firstCourtType);
    } else {
      setCourtType("");
    }
  };

  const handleDurationChange = (change) => {
    setDuration((prev) => {
      const newDuration = Math.max(1, Math.min(2, prev + change));
      if (
        facility &&
        selectedSport &&
        date &&
        (!isFootballOrCricket || courtType)
      ) {
        fetchAvailableSlots(newDuration);
      }
      return newDuration;
    });
  };

  const handleCourtsReqChange = (change) => {
    if (!startTime || availableSlots.length === 0) return;

    const selectedSlot = availableSlots.find(
      (slot) => slot.start_time === startTime
    );
    if (!selectedSlot) return;

    const maxCourts = selectedSlot.available_count;

    setCourtsReq((prev) => {
      const newCourtsReq = prev + change;
      const clampedValue = Math.max(1, Math.min(maxCourts, newCourtsReq));
      return clampedValue;
    });
  };

  const handleDateChange = (newDate) => {
    setDate(newDate);
    setAvailableSlots([]);
    setStartTime("");
    setCourtsReq(0);
    setPlayersReq(0);
  };

  const handleCourtTypeChange = (e) => {
    setCourtType(e.target.value);
    setAvailableSlots([]);
    setStartTime("");
    setCourtsReq(0);
    setPlayersReq(0);
    fetchAvailableSlots();
  };

  const handleStartTimeChange = (time) => {
    const selectedTime = time;
    setStartTime(selectedTime);

    if (selectedTime && facility && selectedSport) {
      const sportDetails = facility.sport_info[selectedSport];
      if (sportDetails) {
        const courtTypeDetails = isFootballOrCricket
          ? sportDetails[courtType]
          : sportDetails;
        if (courtTypeDetails && courtTypeDetails.min_players) {
          const minPlayers = courtTypeDetails.min_players * courtsReq;
          setPlayersReq(minPlayers);
        }
      }
    } else {
      setPlayersReq(0);
    }

    const selectedSlot = availableSlots.find(
      (slot) => formatTime(slot.start_time) === selectedTime
    );
    if (selectedSlot) {
      setCourtsReq(1);
    }
  };

  const handleReserve = async () => {
    if (
      !facility ||
      !selectedSport ||
      !date ||
      !startTime ||
      !playersReq ||
      (["football", "cricket"].includes(selectedSport) && !courtType)
    ) {
      toast.error(
        "Please fill in all required fields and select a valid time slot."
      );
      return;
    }

    if (bringingFriend && friendsList.length === 0) {
      toast.error("Please add friend details or untick the option.");
      return;
    }

    const bookingTime = startTime;
    const bookingDate = formatDate(date);
    const guestIncluded = friendsList.length > 0;

    const bookingData = {
      facility_id: facilityId,
      sport: selectedSport,
      booking_date: bookingDate,
      booking_time: bookingTime,
      players_req: parseInt(playersReq),
      courts_req: courtsReq,
      duration: duration,
      guest_included: guestIncluded,
      guest_list: friendsList.map((friend) => ({
        name: friend.name,
        phone_no: friend.contact.startsWith("+91")
          ? friend.contact
          : `+91 ${friend.contact}`,
      })),
    };

    if (["football", "cricket"].includes(selectedSport)) {
      bookingData.court_type = courtType;
    } else {
      bookingData.court_type = "";
    }
    const accessToken = window.localStorage.getItem("access_token");

    try {
      setLoading(true);
      const response = await fetch(
        "https://ballersapi.ballerslife.co/api/v1/memberships/bookings/make_a_booking",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(bookingData),
        }
      );

      const data = await response.json();
      if (response.status === 200) {
        setBookingId(data.booking_id);
        if (guestIncluded) {
          const gstResponse = await fetch(
            `https://ballersapi.ballerslife.co/api/v1/memberships/bookings/check_gst?booking_id=${data.booking_id}`,
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
              body: "",
            }
          );

          const gstData = await gstResponse.json();
          if (gstResponse.ok) {
            setPaymentDetails(gstData);
            setIsBillModalOpen(true);
          } else {
            toast.error(gstData.detail || "Failed to fetch GST details.");
          }
        } else {
          setShowModal(true);
        }
      } else {
        // setShowModal(true);
        toast.error(data.detail || "Failed to make the booking.");
      }
    } catch (err) {
      console.error("Booking error:", err);
      toast.error("An error occurred while making the booking.");
    } finally {
      setLoading(false);
    }
  };

  const handleFriendsUpdate = (updatedFriends, isBringingFriend) => {
    setFriendsList(updatedFriends);
    setBringingFriend(isBringingFriend);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleBecomeMember = () => {
    setShowModal(false);
    setIsPaymentModalOpen(true);
  };

  const handleCloseBillModal = () => {
    setIsBillModalOpen(false);
    setPaymentDetails(null);
  };

  useEffect(() => {
    fetchFacilityDetails();
  }, [facilityId]);

  if (loading) return <Loader />;
  if (error) return <p>{error}</p>;
  if (!facility) return <p>No facility details found.</p>;

  const isFootballOrCricket = ["football", "cricket"].includes(selectedSport);
  const courtTypes =
    isFootballOrCricket && facility
      ? Object.keys(facility.sport_info[selectedSport] || {})
      : [];

  const isUnpaid = userDetail?.payment_status === "unpaid" && !isTrialActive();
  const reserveButtonText =
    friendsList.length > 0 ? "Pay and Reserve" : "Reserve Now";

  return (
    <>
      <div className="goBackSvgOppor">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="21"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#FFBF00"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-chevron-left"
          onClick={() => navigate("/dashboard/reserve")}
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
      </div>
      <div className="facility-details-venue">
        <div className="facility-info">
          <img
            src={facility.facility_image || areanaImg}
            alt={facility.facility_name}
            className="facility-image"
          />
          <div className="facility-text">
            <h2 className="facility-name">{facility.facility_name}</h2>
            <p className="facility-location">
              {facility.locality}, {facility.city}
              <a
                href={facility.direction_link}
                target="_blank"
                rel="noopener noreferrer"
                className="maps-link"
              >
                <span className="maps-icon"></span>
              </a>
            </p>
            <p className="facility-sports">
              {facility.available_sports
                .map((sport) => sport.charAt(0).toUpperCase() + sport.slice(1))
                .join(", ")}
            </p>
          </div>
        </div>
        <span className="sleepingLine"></span>
        <div className="reservation-form">
          <h3 className="form-title">
            Get Your Game Face On: Reserve Your Court!
          </h3>
          <div className="form-group">
            <label>SPORT</label>
            <div className="select-wrapper">
              <DropDownSport
                options={facility.available_sports}
                selected={selectedSport}
                onChange={setSelectedSport}
              />
            </div>
          </div>

          {isFootballOrCricket && (
            <div className="form-group">
              <label>COURT TYPE</label>
              <div className="select-wrapper">
                <select value={courtType} onChange={handleCourtTypeChange}>
                  {courtTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="form-group">
            <label>DATE</label>
            <div className="input-wrapper">
              <DatePicker
                selected={date}
                onChange={handleDateChange}
                dateFormat="dd/MM/yyyy"
                minDate={new Date()}
                maxDate={maxDate}
                className="date-picker"
                ref={datePickerRef}
              />
              <span
                className="icon calendar-icon"
                onClick={() => datePickerRef.current.setOpen(true)}
              ></span>
            </div>
          </div>

          <div className="form-group">
            <label>DURATION</label>
            <div className="duration-wrapper" id="timeWrapper">
              <button
                onClick={() => handleDurationChange(-1)}
                className="duration-btn"
                disabled={loading}
              >
                −
              </button>
              <span className="duration-value" id="timeWrapper">
                {duration} Hour{duration !== 1 ? "s" : ""}
              </span>
              <button
                onClick={() => handleDurationChange(1)}
                className="duration-btn"
                disabled={loading}
              >
                +
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>START TIME</label>
            <div className="duration-wrapper">
              <TimeDropdown
                startTime={startTime}
                handleStartTimeChange={handleStartTimeChange}
                availableSlots={availableSlots}
                fetchAvailableSlots={fetchAvailableSlots}
                loading={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label>NO OF COURTS</label>
            <div className="duration-wrapper" id="timeWrapper">
              <button
                onClick={() => handleCourtsReqChange(-1)}
                className="duration-btn"
              >
                −
              </button>
              <span className="duration-value" id="timeWrapper">
                {courtsReq}
              </span>
              <button
                onClick={() => handleCourtsReqChange(1)}
                className="duration-btn"
              >
                +
              </button>
            </div>
          </div>
          <div className="form-group">
            <label>NO OF PLAYERS</label>
            <div className="duration-wrapper">
              <button
                onClick={() => handleNoofPlayersChange(-1)}
                className="duration-btn"
                disabled={loading}
              >
                −
              </button>
              <span className="duration-value" id="timeWrapper">
                {playersReq}
              </span>
              <button
                onClick={() => handleNoofPlayersChange(1)}
                className="duration-btn"
                disabled={loading}
              >
                +
              </button>
            </div>
          </div>

          <FriendForm
            onFriendsUpdate={handleFriendsUpdate}
            playersReq={playersReq}
          />
          <button
            className="reserve-btn"
            onClick={handleReserve}
            disabled={loading}
          >
            {loading ? "Processing..." : reserveButtonText}
          </button>
        </div>

        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content-VenueList">
              <h2>{facility.facility_name}</h2>
              {isUnpaid ? (
                <>
                  <p className="modal-message">
                    Hold up, champ! This court is for Ballers only.
                  </p>
                  <p className="modal-submessage">
                    Ballers play unlimited. No per-session fees. No booking
                    stress. Just pure game time.
                  </p>
                  <div className="modal-details">
                    <ul className="modal-benefits">
                      <li>🔥 Members save ₹5L+ over 10 years</li>
                      <li>🔥 Access 10,000+ courts & turfs worldwide</li>
                      <li>🔥 Exclusive tournaments, leaderboards & rewards</li>
                    </ul>
                  </div>
                  <button className="modal-button" onClick={() => openModal()}>
                    Membership Plans
                  </button>
                </>
              ) : (
                <>
                  <p className="modal-message">
                    You're in! Game reserved successfully.
                  </p>
                  <p className="modal-submessage">
                    Note: The game will be confirmed only when all players join.
                  </p>
                  <div className="modal-details">
                    <ul className="modal-benefits">
                      <li>
                        <span
                          style={{
                            fontSize: "1.2rem",
                          }}
                        >
                          📆
                        </span>
                        You can cancel up to 12 hours before the game — no
                        penalty.
                      </li>
                      <li>
                        <span
                          style={{
                            fontSize: "1.2rem",
                          }}
                        >
                          🚫
                        </span>{" "}
                        Cancelling within 12 hours or not showing up will result
                        in a 2-day reduction in your membership.
                      </li>
                    </ul>
                  </div>
                  <button
                    className="modal-button"
                    onClick={() =>
                      navigate("/dashboard/joingameDetails/" + bookingId)
                    }
                  >
                    See you on the Court!
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <PaymentModalStep
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          initialStep={initialStep}
        />
        {isBillModalOpen && (
          <BillingDetailsGuest
            onClose={handleCloseBillModal}
            bookingId={bookingId}
            paymentDetails={paymentDetails}
          />
        )}
        {isModalOpen && (
          <MemberShipPlan onClose={closeModalNew} userDetail={userDetail} />
        )}
      </div>
    </>
  );
}

export default VenueListDetails;
